import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import { Input, Popover, Segmented, Avatar } from 'antd'
import type { InputRef } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { List as VirtualList, ListImperativeAPI } from 'react-window'

import { AppState } from '../../../stores/app'
import { AppService } from '../../../services/app'
import { PlatformUserDomainRecord } from '../../../models/domain/platform-user-domain-record'
import { useOpenAccessPath } from '../../../core/access/use-open-access-path'
import { simpleNotification } from '../../../_utils/utils'
import { AppActionContext } from '../../../enums/app-action-context'

import type { ActiveDomainSelectorProps, OrganizationGroup, ListItemType } from './types'
import {
    LIST_WIDTH,
    DOMAIN_ITEM_HEIGHT,
    ORG_ITEM_HEIGHT,
    MAX_LIST_HEIGHT,
    MIN_LIST_HEIGHT,
    TAB_BAR_HEIGHT,
} from './utils/constants'
import { buildListItems, buildPinnedListItems } from './utils/list-builder'
import { resolveNavigationRoute } from './utils/resolve-route'

import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'

import { useSearch } from './hooks/use-search'
import { useRecents } from './hooks/use-recents'
import { usePinned } from './hooks/use-pinned'
import { useStickyHeader } from './hooks/use-sticky-header'
import { useKeyboardNav } from './hooks/use-keyboard-nav'

import { StarOutlined, StarFilled } from '@ant-design/icons'

import DomainIcon from './components/domain-icon'

import './style/active-domain-selector.scss'
import { FeatureAnticon } from '../../icons/feature-icon'

// ============================================================================
// Component
// ============================================================================

const notifyError = (message: string) => simpleNotification('error', message)

export const ActiveDomainSelector: React.FC<ActiveDomainSelectorProps> = observer((props) => {
    // ========================================================================
    // Dependency Injection
    // ========================================================================

    const appState = useMemo(() => {
        try {
            return Container.get(AppState)
        } catch (error) {
            console.error('[ActiveDomainSelector] Failed to resolve AppState dependency:', error)
            throw new Error(
                '[ActiveDomainSelector] AppState dependency is not registered in the IoC container. ' +
                    'Ensure AppState is registered before rendering ActiveDomainSelector.',
            )
        }
    }, [])
    // The service decides between a router push and a full load when a new version is out.
    const route = useCallback((path: string) => Container.get(AppService).route(path), [])
    const openAccessPath = useOpenAccessPath(route, notifyError)

    // ========================================================================
    // State
    // ========================================================================

    const [visible, setVisible] = useState(false)
    const [selectionAnnouncement, setSelectionAnnouncement] = useState('')
    const [activeTab, setActiveTab] = useState<'all' | 'pinned'>('all')

    const listRef = useRef<ListImperativeAPI | null>(null)
    const searchInputRef = useRef<InputRef>(null)

    // ========================================================================
    // Computed Values
    // ========================================================================

    const orgMap = useMemo((): Map<number, OrganizationGroup> => {
        const domains = appState.currentUserDomains
        if (!domains || domains.length === 0) return new Map()

        const orgGroups = new Map<number, PlatformUserDomainRecord[]>()
        for (const domain of domains) {
            if (!orgGroups.has(domain.accountId)) {
                orgGroups.set(domain.accountId, [])
            }
            orgGroups.get(domain.accountId)!.push(domain)
        }

        const map = new Map<number, OrganizationGroup>()
        for (const [accountId, domainList] of orgGroups) {
            domainList.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()))
            map.set(accountId, {
                accountId,
                accountName: domainList[0].accountName,
                domains: domainList,
            })
        }
        return map
    }, [appState.currentUserDomains])

    const domainMap = useMemo((): Map<number, PlatformUserDomainRecord> => {
        const domains = appState.currentUserDomains
        if (!domains || domains.length === 0) return new Map()

        const map = new Map<number, PlatformUserDomainRecord>()
        for (const domain of domains) {
            map.set(domain.id, domain)
        }
        return map
    }, [appState.currentUserDomains])

    // ========================================================================
    // Hooks
    // ========================================================================

    const {
        query,
        setQuery,
        debouncedQuery,
        filteredDomains: getFilteredDomains,
    } = useSearch(appState.currentUserDomains)

    const userPreferences = useUserPreferences(appState.currentUser?.id)

    const { recents, addToRecents } = useRecents(userPreferences, domainMap, props.maxRecents ?? 3)

    const { favorited, togglePin } = usePinned(userPreferences, domainMap, props.maxPinned ?? 10)

    // ========================================================================
    // Derived Values
    // ========================================================================

    const filteredDomains = useMemo(
        () => getFilteredDomains(activeTab, favorited),
        [getFilteredDomains, activeTab, favorited],
    )

    const listItems = useMemo((): ListItemType[] => {
        if (activeTab === 'pinned') {
            return buildPinnedListItems(filteredDomains, appState.currentDomain?.id)
        }
        return buildListItems(
            filteredDomains,
            orgMap,
            recents,
            appState.currentDomain?.id,
            props.showOrganizations ?? true,
            debouncedQuery,
            appState.abilityStore,
            domainMap,
        )
    }, [
        filteredDomains,
        orgMap,
        recents,
        appState.currentDomain?.id,
        props.showOrganizations,
        debouncedQuery,
        activeTab,
    ])

    const getItemSize = (index: number): number => {
        const item = listItems[index]
        if (!item) return DOMAIN_ITEM_HEIGHT
        switch (item.type) {
            case 'org-header':
            case 'recents-header':
                return ORG_ITEM_HEIGHT
            case 'domain':
            case 'empty':
                return DOMAIN_ITEM_HEIGHT
            default:
                return DOMAIN_ITEM_HEIGHT
        }
    }

    const listHeight = useMemo((): number => {
        let totalHeight = 0
        for (let i = 0; i < listItems.length; i++) {
            totalHeight += getItemSize(i)
        }
        return Math.max(MIN_LIST_HEIGHT, Math.min(totalHeight, MAX_LIST_HEIGHT))
    }, [listItems]) // eslint-disable-line react-hooks/exhaustive-deps

    const searchResultAnnouncement = useMemo(() => {
        if (!debouncedQuery.trim()) return ''
        const tabLabel = activeTab === 'pinned' ? 'pinned ' : ''
        return `${filteredDomains.length} ${tabLabel}domain${filteredDomains.length !== 1 ? 's' : ''} found`
    }, [filteredDomains, debouncedQuery, activeTab])

    const { stickyOrgHeader } = useStickyHeader(listRef, listItems, activeTab, getItemSize, visible)

    // ========================================================================
    // Selection Handlers (defined before useKeyboardNav which needs them)
    // ========================================================================

    const handleSelectDomain = async (domain: PlatformUserDomainRecord, ev?: React.MouseEvent) => {
        const isMetaClick = ev?.metaKey ?? false

        if (!isMetaClick) {
            addToRecents(domain.id)
            setVisible(false)
            setQuery('')

            setSelectionAnnouncement(`Selected ${domain.displayName}`)
            setTimeout(() => setSelectionAnnouncement(''), 1000)
        }

        const nextLocation = resolveNavigationRoute({
            isOrgNode: false,
            entityId: domain.id,
            currentPathname: location.pathname,
            isContextSwap: appState.inOrgContext,
        })

        if (isMetaClick) {
            if (nextLocation) {
                window.open(`${appState.platformHost}${nextLocation}`)
            }
            return
        }

        // A route outside a domain or organization has no sub-route to keep: open the dashboard.
        await openAccessPath(nextLocation ?? `/domains/${domain.id}/dashboard`)
    }

    const handleSelectOrg = async (
        accountId: number,
        accountName: string,
        canAccess: boolean,
        ev?: React.MouseEvent,
    ) => {
        if (!canAccess) return

        const isMetaClick = ev?.metaKey ?? false
        const org = orgMap.get(accountId)
        if (!org || org.domains.length === 0) return

        const firstDomain = org.domains[0]

        if (!isMetaClick) {
            setVisible(false)
            setQuery('')
        }

        const nextLocation = resolveNavigationRoute({
            isOrgNode: true,
            entityId: accountId,
            currentPathname: location.pathname,
            isContextSwap: !appState.inOrgContext,
        })

        if (isMetaClick) {
            if (nextLocation) {
                window.open(`${appState.platformHost}${nextLocation}`)
            }
            return
        }

        // The organization URL opens the organization's first domain; outside an entity route there is no
        // organization page to show, so that domain's dashboard opens.
        await openAccessPath(nextLocation ?? `/domains/${firstDomain.id}/dashboard`)
    }

    // ========================================================================
    // Keyboard Navigation
    // ========================================================================

    const { focusedIndex, setFocusedIndex } = useKeyboardNav(listItems, listRef, visible, {
        onSelectDomain: handleSelectDomain,
        onSelectOrg: handleSelectOrg,
        onTogglePin: togglePin,
        onClose: () => setVisible(false),
    })

    // ========================================================================
    // Remaining Event Handlers (need setFocusedIndex)
    // ========================================================================

    const handleOpenChange = (open: boolean) => {
        setVisible(open)

        if (open) {
            setTimeout(() => {
                searchInputRef.current?.focus()

                // Auto-highlight first interactive item so keyboard users
                // immediately see where they are in the list
                const firstInteractive = listItems.findIndex(
                    (item) => item.type === 'domain' || item.type === 'org-header',
                )
                setFocusedIndex(firstInteractive >= 0 ? firstInteractive : -1)
            }, 100)
        } else {
            setQuery('')
            setFocusedIndex(-1)
            setActiveTab('all')
        }
    }

    const handleTabChange = (key: string) => {
        setActiveTab(key as 'all' | 'pinned')
        setFocusedIndex(-1)
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value)
        setFocusedIndex(-1)
    }

    const activeDescendantId = useMemo(() => {
        if (focusedIndex < 0 || focusedIndex >= listItems.length) return undefined
        const item = listItems[focusedIndex]
        if (item.type === 'domain') return `domain-option-${item.domain.id}`
        return undefined
    }, [focusedIndex, listItems])

    // ========================================================================
    // Lifecycle Effects
    // ========================================================================

    useEffect(() => {
        if (!visible) return

        const timer = setTimeout(() => {
            if (listRef.current) {
                listRef.current.scrollToRow({ index: 0, align: 'start' })
            }
        }, 50)

        return () => clearTimeout(timer)
    }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll to top when search query or tab changes
    useEffect(() => {
        if (!visible) return
        if (listRef.current) {
            listRef.current.scrollToRow({ index: 0, align: 'start' })
        }
    }, [debouncedQuery, activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

    // ========================================================================
    // Row Props — passed through react-window's rowProps so the stable
    // RowComponent can read changing values without being remounted.
    // ========================================================================

    const rowData = useMemo(
        () => ({
            listItems,
            focusedIndex,
            activeDomainId: appState.currentDomain?.id,
            favorited,
            activeTab,
            debouncedQuery,
            hasNoDomains: !appState.currentUserDomains || appState.currentUserDomains.length === 0,
            showOrganizations: props.showOrganizations ?? true,
            maxPinned: props.maxPinned ?? 10,
            onSelectDomain: handleSelectDomain,
            onSelectOrg: handleSelectOrg,
            onMouseEnter: setFocusedIndex,
            onTogglePin: togglePin,
        }),
        [
            listItems,
            focusedIndex,
            appState.currentDomain?.id,
            favorited,
            activeTab,
            debouncedQuery,
            appState.currentUserDomains,
            props.showOrganizations,
            props.maxPinned,
        ], // eslint-disable-line react-hooks/exhaustive-deps
    )

    // ========================================================================
    // Stable Row Component — defined once via useRef so react-window never
    // unmounts/remounts rows. All changing data comes through rowProps.
    // ========================================================================

    const RowComponent = useRef(({ index, style, ...rp }: any) => {
        const data = rp as typeof rowData
        const item = data.listItems[index]
        if (!item) return null

        const isActive = item.type === 'domain' && !item.inRecents && item.domain.id === data.activeDomainId
        const isFocused = index === data.focusedIndex

        // Organization header
        if (item.type === 'org-header') {
            const orgHeaderClasses = [
                'domain-list-org-header',
                !item.canAccess && 'domain-list-org-header-disabled',
                isFocused && 'domain-list-org-header-focused',
            ]
                .filter(Boolean)
                .join(' ')

            return (
                <div
                    key={`org-${item.accountId}`}
                    style={style}
                    className={orgHeaderClasses}
                    role="presentation"
                    onClick={(e) => data.onSelectOrg(item.accountId, item.accountName, item.canAccess, e)}
                    onMouseEnter={() => data.onMouseEnter(index)}
                >
                    <div className="org-header-content">
                        <span className="org-name">{item.accountName}</span>
                        <span className="org-label">Organization</span>
                    </div>
                </div>
            )
        }

        // Recents header
        if (item.type === 'recents-header') {
            return (
                <div key="recents-header" style={style} className="domain-list-recents-header" role="presentation">
                    <div className="recents-header-content">
                        <span className="recents-label">Recent</span>
                    </div>
                </div>
            )
        }

        // Empty state
        if (item.type === 'empty') {
            let emptyMessage: string
            let emptyHint: string | undefined

            if (data.activeTab === 'pinned') {
                emptyMessage = data.debouncedQuery.trim() ? 'No pinned domains match' : 'No pinned domains'
                emptyHint = !data.debouncedQuery.trim() ? 'Pin domains from the All tab for quick access' : undefined
            } else if (data.hasNoDomains) {
                emptyMessage = 'No domains available'
            } else {
                emptyMessage = 'No domains found'
            }

            return (
                <div key="empty" style={style} className="domain-list-empty" role="presentation">
                    <div className="empty-content">
                        <span className="empty-message">{emptyMessage}</span>
                        {emptyHint && <span className="empty-hint">{emptyHint}</span>}
                    </div>
                </div>
            )
        }

        // Domain item
        if (item.type === 'domain') {
            const { domain } = item
            const isPinned = data.favorited.includes(domain.id)
            const atCapacity = data.favorited.length >= data.maxPinned
            const pinDisabled = !isPinned && atCapacity

            const domainItemClasses = [
                'domain-list-item',
                isActive && 'domain-list-item-active',
                isFocused && 'domain-list-item-focused',
            ]
                .filter(Boolean)
                .join(' ')

            const pinToggleClasses = [
                'pin-toggle',
                data.activeTab === 'all' && !isPinned && 'pin-toggle-hover-only',
                isPinned && 'pin-toggle-pinned',
                pinDisabled && 'pin-toggle-disabled',
            ]
                .filter(Boolean)
                .join(' ')

            return (
                <div
                    key={`domain-${domain.id}`}
                    id={`domain-option-${domain.id}`}
                    style={style}
                    className={domainItemClasses}
                    onClick={(e) => data.onSelectDomain(domain, e)}
                    onMouseEnter={() => data.onMouseEnter(index)}
                    role="option"
                    aria-selected={isActive}
                >
                    <div className="domain-item-content">
                        <div className="domain-avatar">
                            <DomainIcon key={domain.id} domain={domain} />
                        </div>
                        <div className="domain-info">
                            <div className="domain-title" title={domain.displayName}>
                                {domain.displayName}
                            </div>
                            <div
                                className="domain-description"
                                title={!data.showOrganizations ? domain.accountName : domain.name}
                            >
                                {!data.showOrganizations ? domain.accountName : domain.name}
                            </div>
                        </div>
                        <div
                            className={pinToggleClasses}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (!pinDisabled) {
                                    data.onTogglePin(domain.id)
                                }
                            }}
                            role="button"
                            aria-label={isPinned ? `Unpin ${domain.displayName}` : `Pin ${domain.displayName}`}
                            aria-keyshortcuts="p"
                            tabIndex={-1}
                        >
                            {isPinned ? <StarFilled /> : <StarOutlined />}
                        </div>
                    </div>
                </div>
            )
        }

        return null
    }).current

    // ========================================================================
    // Render
    // ========================================================================

    const { currentDomain } = appState

    return (
        <div className="active-domain-selector">
            <Popover
                trigger="click"
                placement="rightTop"
                open={visible}
                onOpenChange={handleOpenChange}
                overlayClassName={`active-domain-selector-popover${
                    appState.inOrgContext ? ' active-domain-selector-popover-org' : ''
                }`}
                arrow={false}
                align={{ offset: [8, 0] }}
                styles={{
                    container: { padding: 0, background: 'none', boxShadow: 'none', borderRadius: 0 },
                }}
                content={
                    <div className="active-domain-list-container" style={{ width: LIST_WIDTH }}>
                        <div className="active-domain-search-wrapper">
                            <Input
                                ref={searchInputRef}
                                className="active-domain-search"
                                size="small"
                                placeholder={currentDomain?.displayName}
                                suffix={<SearchOutlined />}
                                value={query}
                                onChange={handleSearchChange}
                                role="combobox"
                                aria-expanded={visible}
                                aria-controls="domain-selector-listbox"
                                aria-activedescendant={activeDescendantId}
                                aria-autocomplete="list"
                            />
                        </div>

                        <div className="active-domain-tab-bar" style={{ height: TAB_BAR_HEIGHT }}>
                            <Segmented
                                value={activeTab}
                                onChange={(val) => {
                                    handleTabChange(val as string)
                                    setTimeout(() => {
                                        searchInputRef.current?.focus()
                                    }, 0)
                                }}
                                size="small"
                                block
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'pinned', label: 'Favorites' },
                                ]}
                            />
                        </div>

                        <div
                            className="active-domain-list-scroll-wrapper"
                            style={{ height: listHeight, width: LIST_WIDTH, position: 'relative' }}
                            onMouseLeave={() => setFocusedIndex(-1)}
                        >
                            {stickyOrgHeader && activeTab === 'all' && (
                                <div
                                    className={[
                                        'sticky-org-header-overlay',
                                        focusedIndex >= 0 &&
                                            listItems[focusedIndex]?.type === 'org-header' &&
                                            (listItems[focusedIndex] as any).accountId === stickyOrgHeader.accountId &&
                                            'sticky-org-header-focused',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={(e) =>
                                        handleSelectOrg(
                                            stickyOrgHeader.accountId,
                                            stickyOrgHeader.accountName,
                                            stickyOrgHeader.canAccess,
                                            e,
                                        )
                                    }
                                >
                                    <div className="org-header-content">
                                        <span className="org-name">{stickyOrgHeader.accountName}</span>
                                        <span className="org-label">Organization</span>
                                    </div>
                                </div>
                            )}
                            <div
                                id="domain-selector-listbox"
                                role="listbox"
                                style={{ height: listHeight, width: LIST_WIDTH, position: 'relative' }}
                            >
                                <VirtualList
                                    listRef={listRef}
                                    rowCount={listItems.length}
                                    rowHeight={(index: number) => getItemSize(index)}
                                    overscanCount={20}
                                    rowComponent={RowComponent}
                                    rowProps={rowData}
                                />
                            </div>
                        </div>

                        {/* Screen reader announcements */}
                        <div
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            style={{
                                position: 'absolute',
                                width: '1px',
                                height: '1px',
                                padding: 0,
                                margin: '-1px',
                                overflow: 'hidden',
                                clip: 'rect(0, 0, 0, 0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                            }}
                        >
                            {searchResultAnnouncement}
                        </div>
                        <div
                            aria-live="assertive"
                            aria-atomic="true"
                            style={{
                                position: 'absolute',
                                width: '1px',
                                height: '1px',
                                padding: 0,
                                margin: '-1px',
                                overflow: 'hidden',
                                clip: 'rect(0, 0, 0, 0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                            }}
                        >
                            {selectionAnnouncement}
                        </div>
                    </div>
                }
            >
                <div
                    className="active-domain-select-item active-domain-select-item-selected"
                    tabIndex={0}
                    role="button"
                    aria-label={
                        appState.inOrgContext
                            ? `Current organization: ${currentDomain?.accountName}. Press Enter to switch.`
                            : `Current domain: ${currentDomain?.displayName}. Press Enter to switch.`
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleOpenChange(true)
                        }
                    }}
                >
                    <div className="domain-item-content">
                        <div className="domain-avatar">
                            {appState.inOrgContext ? (
                                <Avatar icon={<FeatureAnticon slot="rail:org:settings" />} gap={2} />
                            ) : (
                                <DomainIcon domain={currentDomain} />
                            )}
                        </div>
                        <div className="domain-info">
                            <div
                                className="domain-title"
                                title={appState.inOrgContext ? currentDomain?.accountName : currentDomain?.displayName}
                            >
                                {appState.inOrgContext ? currentDomain?.accountName : currentDomain?.displayName}
                            </div>
                            <div
                                className="domain-description"
                                title={appState.inOrgContext ? 'Organization' : currentDomain?.name}
                            >
                                {appState.inOrgContext ? 'Organization' : currentDomain?.name}
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>
        </div>
    )
})

// Re-export error boundary as default export for external consumers
export { default } from './components/error-boundary'
